// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { usePositionForm } from '@client/banks/hooks/use-position-form';
import type { BankPosition } from '@client/banks/types/position';
import {
  createEmptyBankPosition,
  getDefaultBankCurrency,
} from '@client/banks/utils/bank-form-defaults';
import { commitInstrumentWithLinkedAccount } from '@client/banks/utils/commit-instrument-with-linked-account';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo, useRef, useState } from 'react';

export const usePositionsForm = () => {
  const { navigate } = useStepsData();
  const {
    formData,
    listEditor,
    currencyOptions,
    accountIbanOptions,
    replaceFormData,
    closeListEditor,
  } = useBankUploadData();

  const linkedLiquidityApiRef = useRef<LinkedLiquiditySectionApi | null>(null);

  const [linkedLiquidityGate, setLinkedLiquidityGate] =
    useState<LinkedLiquidityGate>({
      isCreateMode: false,
      isAccountFormValid: true,
    });

  const positions = useMemo(() => formData.positions ?? [], [formData.positions]);

  const defaultCurrency = useMemo(
    () => getDefaultBankCurrency(currencyOptions),
    [currencyOptions],
  );

  const defaultValues = useMemo(() => {
    const emptyPosition = createEmptyBankPosition(defaultCurrency);

    if (listEditor?.section !== 'positions') {
      return emptyPosition;
    }

    if (listEditor.index < positions.length) {
      return { ...positions[listEditor.index] };
    }

    return emptyPosition;
  }, [defaultCurrency, listEditor, positions]);

  const form = usePositionForm({
    variant: 'create',
    defaultValues,
  });

  const isFormValid = useCallback(() => {
    if (!form.formState.isValid) {
      return false;
    }

    if (
      linkedLiquidityGate.isCreateMode &&
      !linkedLiquidityGate.isAccountFormValid
    ) {
      return false;
    }

    return true;
  }, [form.formState.isValid, linkedLiquidityGate]);

  const onSubmit = useCallback(
    async (data: BankPosition) => {
      if (!listEditor || listEditor.section !== 'positions') {
        return;
      }

      const linkedResult = await linkedLiquidityApiRef.current?.resolveSubmit();

      if (!linkedResult || linkedResult.mode === 'invalid') {
        return;
      }

      const nextFormData = commitInstrumentWithLinkedAccount(
        formData,
        'positions',
        listEditor.index,
        {
          ...data,
          iban: linkedResult.mode === 'create' ? '' : linkedResult.iban,
        },
        linkedResult.mode === 'create' ? linkedResult.account : undefined,
      );

      replaceFormData(nextFormData);
      closeListEditor();
      navigate('positions-list');
    },
    [closeListEditor, formData, listEditor, navigate, replaceFormData],
  );

  const onCancel = useCallback(() => {
    closeListEditor();
    navigate('positions-list');
  }, [closeListEditor, navigate]);

  return {
    form,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange: setLinkedLiquidityGate,
    isFormValid,
    onSubmit,
    onCancel,
  };
};
